const features = [
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
        />
      </svg>
    ),
    title: "Auditable Decisions",
    description:
      "Every score, LLM output, routing decision, and trade intent is logged with full context. Replay any event to understand exactly why a decision was made.",
    color: "text-amber-400",
    bg: "bg-amber-400/10",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.58-5.84 3.18 3.18"
        />
      </svg>
    ),
    title: "Deterministic Scoring",
    description:
      "The core scoring engine is entirely LLM-free: pure rule-based logic that's testable, reproducible, and configurable with externalized thresholds.",
    color: "text-cyan-400",
    bg: "bg-cyan-400/10",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
        />
      </svg>
    ),
    title: "LLM-Assisted Intelligence",
    description:
      "LLM classification is invoked only for high-priority ANALYZE events. Outputs are schema-validated JSON — directionality, vol risk, and playbook — with raw responses stored for audit.",
    color: "text-violet-400",
    bg: "bg-violet-400/10",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z"
        />
      </svg>
    ),
    title: "Volatility Playbooks",
    description:
      "Events are routed to one of four outcomes: Long Gamma, Directional Breakout, Monitor & Wait, or Stand Down. The system is designed to stand down often — avoiding bad trades is profitable.",
    color: "text-green-400",
    bg: "bg-green-400/10",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
        />
      </svg>
    ),
    title: "Risk Guardrails",
    description:
      "Hard stops enforce maximum risk per event, maximum trades per day, and liquidity constraints. Conflicting event updates trigger automatic stand-down with explicit rejection reasons.",
    color: "text-rose-400",
    bg: "bg-rose-400/10",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z"
        />
      </svg>
    ),
    title: "Event Thread Clustering",
    description:
      "Hash-first de-duplication and intelligent clustering prevent double-triggering on the same news. EventThreads track full evolution — from first signal to last update.",
    color: "text-blue-400",
    bg: "bg-blue-400/10",
  },
];

export default function Features() {
  return (
    <section
      id="features"
      className="py-24 sm:py-32 border-t border-navy-700/60"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest text-huskly-teal mb-4">
            Capabilities
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Built for Decision Quality, Not Speed
          </h2>
          <p className="mt-4 text-slate-400 leading-relaxed">
            Aegis Vol is designed around a deceptively simple principle:
            avoiding bad trades is profitable. Every feature serves that goal.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="rounded-xl border border-navy-700 bg-navy-800/40 p-6 flex flex-col gap-4 hover:border-navy-600 transition-colors"
            >
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${feature.bg} ${feature.color}`}
              >
                {feature.icon}
              </div>
              <div>
                <h3 className="text-base font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
